import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import type { GridFilterModel } from '@mui/x-data-grid';
import { t } from 'i18next';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store/AppStore';
import type { IProperty, ISelectedTreeItem } from '../../../types';
import { IPropertyHistoryDialogProps } from '../../../types/IComponentTypes';
import { IReadPropertyHistoryRequest } from '../../../types/IRequestTypes';
import type { IHistoryFilterPanelProps } from '../../../types/ITypes';
import { EPropertyApplicability } from '../../data/Constants';
import { checkIfMasterProperty } from '../../services/PropertyHelperFunctions';
import {
  getPropertyHistoryData,
  resetPropertyHistory,
} from '../../store/states/PropertyHistorySlice';
import { DataTable } from '../category/DataTable';
import DraggableComponent from '../DraggableComponent';
import { HistoryFilterPanel } from './HistoryFilterPanel';

/**
 * Resolves productCode, featureCode, and optionCode for the history API payload.
 * At header level (no property), only the material's own code is passed.
 * At property level, parent hierarchy codes are included or stripped based on master/product scope.
 */
const resolveMaterialCodes = (
  material: ISelectedTreeItem,
  selectedProperty: IProperty | undefined,
  isMasterProperty: boolean
): {
  productCode: string | null | undefined;
  featureCode: string | null | undefined;
  optionCode: string | null | undefined;
} => {
  let productCode = null;
  let featureCode = null;
  let optionCode = null;
  switch (material.type) {
    case EPropertyApplicability.Product:
    case EPropertyApplicability.Master:
      productCode = material.code;
      break;
    case EPropertyApplicability.Feature:
    case EPropertyApplicability.Numeric:
    case EPropertyApplicability.String:
      // Header level: pass only material.code; property level: include parent productCode
      if (selectedProperty) {
        productCode = isMasterProperty ? undefined : material.productCode;
      }
      featureCode = material.code;
      break;
    case EPropertyApplicability.Option:
      // Header level: pass only material.code; property level: include parent codes
      if (selectedProperty) {
        productCode = isMasterProperty ? undefined : material.productCode;
        featureCode = isMasterProperty ? undefined : material.featureCode;
      }
      optionCode = material.code;
      break;
  }
  return { productCode, featureCode, optionCode };
};

/**
 * Creates a render function for `HistoryFilterPanel` that is bound to a specific
 * `property` context and `fetchPropertyHistory` callback.
 *
 * This factory helps keep the `customFilterPanel` prop stable when passed into
 * `DataTable` (typically via `useMemo`), while still allowing the grid toolbar
 * to inject extra props such as `setPage`, `limit`, and `isReset`.
 *
 * @param property - The selected property context (can be undefined) used to scope
 *                   the filters and labels within the history panel.
 * @param fetchPropertyHistory - Callback invoked by the panel to request history
 *                               data with the current filter state.
 * @returns A component function that renders `HistoryFilterPanel`, merging any
 *          `extraProps` provided by the grid with the bound `property` and `fetchPropertyHistory`.
 *
 */
const createCustomFilterPanel =
  (
    property: IHistoryFilterPanelProps['property'],
    fetchPropertyHistory: IHistoryFilterPanelProps['fetchPropertyHistory']
  ) =>
  (extraProps?: Partial<IHistoryFilterPanelProps>): ReactElement => (
    <HistoryFilterPanel
      property={property}
      fetchPropertyHistory={fetchPropertyHistory}
      {...(extraProps ?? {})}
    />
  );

/**
 * Renders a draggable dialog displaying property history for a selected material and property.
 * Initializes data on mount and wires a custom filter panel into the data table.
 *
 * @param props - Component props containing dialog state, selected context, and callbacks.
 * @returns JSX element rendering the history dialog.
 */
export const HistoryDialog = (props: IPropertyHistoryDialogProps): ReactElement => {
  const { material, tableType, open, property } = props.historyDialog;
  const dispatch = useDispatch<AppDispatch>();

  const [loading, setLoading] = useState(false);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  /**
   * On initial mount, fetches the first page of property history.
   */
  useEffect(() => {
    fetchPropertyHistory({ page: 1, limit: 15 });
  }, []);

  /**
   * Loads property history based on current material/property context and request options.
   * Derives related identifiers from the selected `material` and dispatches the store action.
   *
   * @param request - Pagination and filtering options for history retrieval.
   */
  const fetchPropertyHistory = useCallback(
    (request: IReadPropertyHistoryRequest) => {
      const selectedProperty = props.historyDialog.property;
      const isMasterProperty = selectedProperty ? checkIfMasterProperty(selectedProperty) : false;
      const { productCode, featureCode, optionCode } = material
        ? resolveMaterialCodes(material, selectedProperty, isMasterProperty)
        : { productCode: null, featureCode: null, optionCode: null };
      const propertyHistoryPayload: IReadPropertyHistoryRequest = {
        ...request,
        propertyCode: selectedProperty?.code ?? undefined,
        productCode: productCode ?? undefined,
        featureCode: featureCode ?? undefined,
        optionCode: optionCode ?? undefined,
      };
      setLoading(true);
      dispatch(getPropertyHistoryData(propertyHistoryPayload)).finally(() => setLoading(false));
    },
    [material, props.historyDialog.property?.code, dispatch]
  );

  /**
   * Memoized factory returning the custom filter panel used by the data table.
   * Binds the selected property and the `fetchPropertyHistory` callback.
   *
   * @returns A React component that renders filter controls for the table.
   */
  const customFilterPanel = useMemo(
    () => createCustomFilterPanel(property, fetchPropertyHistory),
    [property, fetchPropertyHistory]
  );

  /**
   * Handles dialog close by clearing local filters, resetting history state, and invoking parent close.
   */
  const handleClose = (): void => {
    props.onClose();
    setFilterModel({ items: [] });
    dispatch(resetPropertyHistory());
  };

  return (
    <Dialog
      open={open}
      className="common-dialog-style pdmHistoryDialogStyle cursor-move"
      slotProps={{ paper: { className: 'paper' } }}
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle className="title">
        <span className="start">{`${t('labels.history')}: `}</span>
        {property && <span className="start">{`${property.name} - `}</span>}
        <span className="center">{material.label}</span>
        <IconButton
          className="close"
          data-testid="closeIcon"
          aria-label="close"
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="dialogContent">
        <DataTable
          loading={loading}
          setLoading={setLoading}
          tableType={tableType}
          fetchData={fetchPropertyHistory}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
          customFilterPanel={customFilterPanel}
          hideRowSelectionCheckboxes={true}
        />
      </DialogContent>
    </Dialog>
  );
};
